import { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

type AdminRole = 'none' | 'admin' | 'analytics_viewer';

interface AdminStatus {
  isAdmin: boolean;
  role: AdminRole;
}

let cachedStatus: AdminStatus | null = null;
let checkPromise: Promise<AdminStatus> | null = null;

async function checkAdminStatus(): Promise<AdminStatus> {
  if (cachedStatus !== null) return cachedStatus;
  if (checkPromise) return checkPromise;

  checkPromise = (async () => {
    try {
      const data = await apiClient.admin.checkAdmin();
      if (data.isSuperAdmin === true) {
        cachedStatus = { isAdmin: true, role: 'admin' };
      } else if (data.isAnalyticsViewer === true) {
        cachedStatus = { isAdmin: false, role: 'analytics_viewer' };
      } else {
        cachedStatus = { isAdmin: false, role: 'none' };
      }
    } catch {
      cachedStatus = { isAdmin: false, role: 'none' };
    }
    return cachedStatus!;
  })();

  return checkPromise;
}

/**
 * Returns true only for super admins (ADMIN_IDS).
 * Does NOT include analytics viewers.
 */
export function useIsAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState(cachedStatus?.isAdmin ?? false);

  useEffect(() => {
    checkAdminStatus().then(s => setIsAdmin(s.isAdmin));
  }, []);

  return isAdmin;
}

/**
 * Returns the role: 'admin', 'analytics_viewer', or 'none'.
 */
export function useAdminRole(): AdminRole {
  const [role, setRole] = useState<AdminRole>(cachedStatus?.role ?? 'none');

  useEffect(() => {
    checkAdminStatus().then(s => setRole(s.role));
  }, []);

  return role;
}
